import React, { useState, useMemo } from 'react';
import { Layers, Square, Trash2, CheckCircle, MapPin, Merge } from 'lucide-react';

export function MultiSelectionPanel({
  selectedPointIds = [],
  selectedWallIds = [],
  selectedSpaceIds = [],
  points = [],
  walls = [],
  spaces = [],
  componentTypes = [],
  onUpdatePoints,
  onUpdateWalls,
  onUpdateSpaces,
  onDeletePoints,
  onDeleteWalls,
  onDeleteSpaces,
  onMergeWalls,
  onClearSelection,
  embedded = false,
}) {
  const [zValue, setZValue] = useState(2750);

  // Nur anzeigen wenn mehrere Elemente ausgewählt sind
  const pointCount = selectedPointIds.length;
  const wallCount = selectedWallIds.length;
  const spaceCount = selectedSpaceIds.length;

  if (pointCount <= 1 && wallCount <= 1 && spaceCount <= 1) {
    return null;
  }

  // Handler für Punkt-Z-Wert-Zuweisung
  const handlePointZChange = (newZ) => {
    const zVal = parseFloat(newZ) || 2750;
    setZValue(zVal);
    selectedPointIds.forEach(pointId => {
      onUpdatePoints?.(pointId, { z: zVal });
    });
  };

  // Ausgewählte Wände holen
  const selectedWalls = walls.filter(w => selectedWallIds.includes(w.id));

  // Ausgewählte Räume holen
  const selectedSpaces = spaces.filter(s => selectedSpaceIds.includes(s.id));

  // Prüfen ob zwei Wände zusammengeführt werden können (gemeinsamer Punkt)
  const canMergeWalls = useMemo(() => {
    if (wallCount !== 2) return { canMerge: false };

    const wall1 = selectedWalls[0];
    const wall2 = selectedWalls[1];
    if (!wall1 || !wall2) return { canMerge: false };

    // Gemeinsamen Punkt finden
    const wall1Points = [wall1.startPointId, wall1.endPointId];
    const wall2Points = [wall2.startPointId, wall2.endPointId];

    const sharedPoint = wall1Points.find(p => wall2Points.includes(p));

    if (!sharedPoint) return { canMerge: false };

    return {
      canMerge: true,
      wall1Id: wall1.id,
      wall2Id: wall2.id,
      sharedPointId: sharedPoint,
    };
  }, [selectedWalls, wallCount]);

  // Handler für Wandzusammenführung
  const handleMergeWalls = () => {
    if (canMergeWalls.canMerge && onMergeWalls) {
      onMergeWalls(canMergeWalls.wall1Id, canMergeWalls.wall2Id);
      onClearSelection?.();
    }
  };

  // Handler für Wand-Typ-Zuweisung
  const handleWallTypeChange = (typeRef) => {
    selectedWallIds.forEach(wallId => {
      onUpdateWalls?.(wallId, { typeRef: typeRef || null });
    });
  };

  // Handler für Boden-Typ-Zuweisung
  const handleFloorTypeChange = (typeRef) => {
    selectedSpaceIds.forEach(spaceId => {
      onUpdateSpaces?.(spaceId, { floorTypeRef: typeRef || null });
    });
  };

  // Handler für Decken-Typ-Zuweisung
  const handleCeilingTypeChange = (typeRef) => {
    selectedSpaceIds.forEach(spaceId => {
      onUpdateSpaces?.(spaceId, { ceilingTypeRef: typeRef || null });
    });
  };

  // Handler für Löschen
  const handleDeleteSelected = () => {
    const totalCount = pointCount + wallCount + spaceCount;
    if (confirm(`${totalCount} Elemente löschen?`)) {
      selectedPointIds.forEach(id => onDeletePoints?.(id));
      selectedWallIds.forEach(id => onDeleteWalls?.(id));
      selectedSpaceIds.forEach(id => onDeleteSpaces?.(id));
      onClearSelection?.();
    }
  };

  // Bauteiltypen nach Kategorie
  const wallTypes = componentTypes.filter(t => t.category === 'wall');
  const floorTypes = componentTypes.filter(t => t.category === 'floor');
  const ceilingTypes = componentTypes.filter(t => t.category === 'ceiling');

  const content = (
    <div className="p-3 space-y-4">
        {/* Auswahl-Info */}
        <div className="flex gap-4 text-sm flex-wrap">
          {pointCount > 0 && (
            <div className="flex items-center gap-1 text-blue-600">
              <MapPin size={16} />
              <span>{pointCount} Punkte</span>
            </div>
          )}
          {wallCount > 0 && (
            <div className="flex items-center gap-1 text-orange-600">
              <Layers size={16} />
              <span>{wallCount} Wände</span>
            </div>
          )}
          {spaceCount > 0 && (
            <div className="flex items-center gap-1 text-green-600">
              <Square size={16} />
              <span>{spaceCount} Räume</span>
            </div>
          )}
        </div>

        {/* Punkt-Höhe (Z-Wert) Zuweisung */}
        {pointCount > 1 && (
          <div>
            <label className="text-xs text-gray-500 font-medium">
              Höhe (Z) für alle {pointCount} Punkte (mm)
            </label>
            <div className="flex gap-2 mt-1">
              <input
                type="number"
                value={zValue}
                onChange={(e) => setZValue(parseFloat(e.target.value) || 2750)}
                className="flex-1 px-2 py-1.5 border rounded text-sm"
                step={100}
              />
              <button
                onClick={() => handlePointZChange(zValue)}
                className="px-3 py-1.5 bg-blue-500 text-white rounded text-sm hover:bg-blue-600"
              >
                Anwenden
              </button>
            </div>
            <div className="flex gap-1 mt-2">
              {[2500, 2750, 3000, 3500].map(h => (
                <button
                  key={h}
                  onClick={() => handlePointZChange(h)}
                  className="flex-1 px-2 py-1 text-xs bg-gray-100 hover:bg-gray-200 rounded"
                >
                  {h / 1000}m
                </button>
              ))}
            </div>
          </div>
        )}

        {/* Wand-Typ Zuweisung */}
        {wallCount > 0 && (
          <div>
            <label className="text-xs text-gray-500 font-medium">
              Wandtyp für alle {wallCount} Wände
            </label>
            <select
              onChange={(e) => handleWallTypeChange(e.target.value)}
              className="w-full px-2 py-1.5 border rounded text-sm mt-1"
              defaultValue=""
            >
              <option value="" disabled>- Typ auswählen -</option>
              <option value="">Kein Typ</option>
              {wallTypes.map(type => (
                <option key={type.id} value={type.id}>
                  {type.shortCode || type.name}
                </option>
              ))}
            </select>
          </div>
        )}

        {/* Wände zusammenführen (Issue #11) */}
        {canMergeWalls.canMerge && (
          <button
            onClick={handleMergeWalls}
            className="w-full flex items-center justify-center gap-2 px-3 py-2 bg-blue-50 text-blue-600 rounded hover:bg-blue-100 border border-blue-200"
          >
            <Merge size={16} />
            Wände zusammenführen
          </button>
        )}

        {/* Hinweis wenn 2 Wände ohne gemeinsamen Punkt */}
        {wallCount === 2 && !canMergeWalls.canMerge && (
          <div className="text-xs text-amber-600 bg-amber-50 p-2 rounded">
            Die Wände haben keinen gemeinsamen Punkt und können nicht zusammengeführt werden.
          </div>
        )}

        {/* Boden-Typ Zuweisung */}
        {spaceCount > 0 && (
          <>
            <div>
              <label className="text-xs text-gray-500 font-medium">
                Bodentyp für alle {spaceCount} Räume
              </label>
              <select
                onChange={(e) => handleFloorTypeChange(e.target.value)}
                className="w-full px-2 py-1.5 border rounded text-sm mt-1"
                defaultValue=""
              >
                <option value="" disabled>- Typ auswählen -</option>
                <option value="">Kein Typ</option>
                {floorTypes.map(type => (
                  <option key={type.id} value={type.id}>
                    {type.shortCode || type.name}
                  </option>
                ))}
              </select>
            </div>

            {/* Decken-Typ Zuweisung */}
            <div>
              <label className="text-xs text-gray-500 font-medium">
                Deckentyp für alle {spaceCount} Räume
              </label>
              <select
                onChange={(e) => handleCeilingTypeChange(e.target.value)}
                className="w-full px-2 py-1.5 border rounded text-sm mt-1"
                defaultValue=""
              >
                <option value="" disabled>- Typ auswählen -</option>
                <option value="">Kein Typ</option>
                {ceilingTypes.map(type => (
                  <option key={type.id} value={type.id}>
                    {type.shortCode || type.name}
                  </option>
                ))}
              </select>
            </div>
          </>
        )}

      {/* Löschen */}
      <button
        onClick={handleDeleteSelected}
        className="w-full flex items-center justify-center gap-2 px-3 py-2 bg-red-50 text-red-600 rounded hover:bg-red-100"
      >
        <Trash2 size={16} />
        Alle löschen ({pointCount + wallCount + spaceCount})
      </button>
    </div>
  );

  // Im embedded-Modus direkt den Inhalt zurückgeben
  if (embedded) {
    return content;
  }

  // Standalone mit eigenem Container
  return (
    <div
      className="absolute right-4 top-4 w-72 bg-white rounded-lg shadow-lg z-10"
      onMouseDown={(e) => e.stopPropagation()}
      onClick={(e) => e.stopPropagation()}
    >
      {/* Header */}
      <div className="flex items-center justify-between p-3 border-b bg-blue-50">
        <div className="flex items-center gap-2">
          <CheckCircle size={18} className="text-blue-600" />
          <span className="font-semibold text-gray-800">
            Mehrfachauswahl
          </span>
        </div>
        <button
          onClick={onClearSelection}
          className="text-xs text-gray-500 hover:text-gray-700"
        >
          Aufheben
        </button>
      </div>
      {content}
    </div>
  );
}
